import { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  incidentArtifactService,
  ARTIFACT_KINDS,
} from '../../../services/incidentArtifactService';
import { trackIncidentArtifactChange } from '../../../utils/modernUITracking';
import styles from './IncidentArtifactLinks.module.css';

const ROWS = [
  { kind: ARTIFACT_KINDS.EXEC_SUMMARY, label: 'Exec Summary link' },
  { kind: ARTIFACT_KINDS.PIR, label: 'PIR link' },
];

const emptyRow = { saved: '', draft: '', busy: false, error: '', notice: '' };

/**
 * IncidentArtifactLinks
 *
 * Lets an Eternium member attach, replace or clear the Exec Summary and PIR links of a
 * resolved incident (US2163904). The parent renders it only for a user who may edit and an
 * incident that is resolved; the server enforces both again, so a message from it is shown
 * as-is. Past Incidents reads these links to show its Exec Summary icon.
 *
 * Render it with key={problemId}: a save still in flight for one incident must not land in
 * another incident's editor after navigation.
 *
 * @param {Object} props
 * @param {string} props.problemId - Problem number
 * @param {Object} [props.newrelic] - NewRelic browser agent instance
 */
function IncidentArtifactLinks({ problemId, newrelic }) {
  const [rows, setRows] = useState({
    [ARTIFACT_KINDS.EXEC_SUMMARY]: emptyRow,
    [ARTIFACT_KINDS.PIR]: emptyRow,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);

  const patchRow = useCallback((kind, patch) => {
    setRows((prev) => ({ ...prev, [kind]: { ...prev[kind], ...patch } }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    incidentArtifactService
      .getArtifacts(problemId)
      .then((list) => {
        if (cancelled) return;
        const next = {
          [ARTIFACT_KINDS.EXEC_SUMMARY]: emptyRow,
          [ARTIFACT_KINDS.PIR]: emptyRow,
        };
        list.forEach((a) => {
          if (next[a.kind]) next[a.kind] = { ...emptyRow, saved: a.externalUrl, draft: a.externalUrl };
        });
        setRows(next);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [problemId, loadAttempt]);

  const handleSave = async (kind) => {
    patchRow(kind, { busy: true, error: '', notice: '' });
    try {
      const saved = await incidentArtifactService.saveArtifact(problemId, kind, rows[kind].draft);
      patchRow(kind, { busy: false, saved: saved.externalUrl, draft: saved.externalUrl, notice: 'Saved' });
      trackIncidentArtifactChange(problemId, kind, 'save', 'ok', newrelic);
    } catch (err) {
      patchRow(kind, { busy: false, error: err.message });
      trackIncidentArtifactChange(problemId, kind, 'save', 'error', newrelic);
    }
  };

  const handleClear = async (kind) => {
    patchRow(kind, { busy: true, error: '', notice: '' });
    try {
      await incidentArtifactService.clearArtifact(problemId, kind);
      patchRow(kind, { busy: false, saved: '', draft: '', notice: 'Removed' });
      trackIncidentArtifactChange(problemId, kind, 'clear', 'ok', newrelic);
    } catch (err) {
      patchRow(kind, { busy: false, error: err.message });
      trackIncidentArtifactChange(problemId, kind, 'clear', 'error', newrelic);
    }
  };

  // A failed load leaves every row empty, which would read as "no link" and let a Save replace
  // one the user never saw. Nothing is editable until the stored links have loaded.
  const rowsLocked = loading || loadError !== '';

  return (
    <section className={styles.container} aria-labelledby="incident-artifact-links-title">
      <h2 id="incident-artifact-links-title" className={styles.title}>
        Exec Summary and PIR links
      </h2>
      <p className={styles.help}>
        Only Eternium sees and edits these links. Links must be on coxautoinc.sharepoint.com.
      </p>

      {loadError && (
        <>
          <p className={styles.error} role="alert">
            {loadError}
          </p>
          <Button variant="outline-secondary" size="sm" onClick={() => setLoadAttempt((n) => n + 1)}>
            Try again
          </Button>
        </>
      )}

      {ROWS.map(({ kind, label }) => {
        const row = rows[kind];
        const inputId = `incident-artifact-${kind}`;
        const unchanged = row.draft.trim() === row.saved;
        return (
          <div className={styles.row} key={kind}>
            <label htmlFor={inputId} className={styles.label}>
              {label}
            </label>
            <div className={styles.controls}>
              <input
                id={inputId}
                type="url"
                className={styles.input}
                value={row.draft}
                placeholder="https://coxautoinc.sharepoint.com/..."
                disabled={rowsLocked || row.busy}
                onChange={(e) => patchRow(kind, { draft: e.target.value, error: '', notice: '' })}
              />
              <Button
                variant="primary"
                size="sm"
                className={styles.button}
                disabled={rowsLocked || row.busy || unchanged || row.draft.trim() === ''}
                onClick={() => handleSave(kind)}
                aria-label={`Save ${label}`}
              >
                Save
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className={styles.button}
                disabled={rowsLocked || row.busy || row.saved === ''}
                onClick={() => handleClear(kind)}
                aria-label={`Clear ${label}`}
              >
                Clear
              </Button>
            </div>
            {row.error && (
              <p className={styles.error} role="alert">
                {row.error}
              </p>
            )}
            {row.notice && !row.error && (
              <p className={styles.notice} role="status">
                {row.notice}
              </p>
            )}
          </div>
        );
      })}
    </section>
  );
}

export default IncidentArtifactLinks;
